import { Injectable, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, timer } from 'rxjs';

/** Atraso antes da primeira checagem de atualização após o boot do app. */
const PRIMEIRA_VERIFICACAO_MS = 30 * 1000;
/** Intervalo entre checagens periódicas de atualização. */
const INTERVALO_VERIFICACAO_MS = 6 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly snackBar = inject(MatSnackBar);

  initialize(): void {
    if (!this.swUpdate.isEnabled) return;

    this.listenForUpdates();
    this.listenForUnrecoverableState();
    this.scheduleUpdateChecks();
  }

  private listenForUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        const snackBarRef = this.snackBar.open(
          'Nova versão do Nexus disponível',
          'Atualizar',
          { duration: 0 }
        );

        snackBarRef.onAction().subscribe(() => {
          document.location.reload();
        });
      });
  }

  /**
   * Após um deploy, o service worker pode ficar em estado irrecuperável:
   * o ngsw.json antigo referencia chunks com hash que não existem mais no
   * servidor. Sem este tratamento, o usuário fica preso em carregamentos
   * repetidos/lentos até o cache expirar (ou precisa usar janela anônima).
   * A recomendação oficial do Angular é recarregar a página, forçando o
   * SW a buscar a versão íntegra da rede.
   */
  private listenForUnrecoverableState(): void {
    this.swUpdate.unrecoverable.subscribe((event) => {
      console.warn('[AppUpdateService] SW em estado irrecuperável, recarregando:', event.reason);
      document.location.reload();
    });
  }

  /**
   * Usa `timer` (tempo relativo ao boot) em vez de `ApplicationRef.isStable` — este último
   * pode nunca emitir `true` neste app, já que há `interval()`/`watchPosition()` de longa
   * duração (monitor de sessão, rastreamento de geolocalização) que mantêm o Angular
   * "instável" indefinidamente, atrasando (ou impedindo) a checagem de nova versão do SW.
   */
  private scheduleUpdateChecks(): void {
    timer(PRIMEIRA_VERIFICACAO_MS, INTERVALO_VERIFICACAO_MS).subscribe(async () => {
      try {
        await this.swUpdate.checkForUpdate();
      } catch (err) {
        console.error('[AppUpdateService] Erro ao verificar atualização:', err);
      }
    });
  }
}
